/**
 * Implementation based on the SPIKE https://docs.google.com/document/d/1KOI1m6tmoQ0tp2yAbiraNKeBI1_WkQhnP_Lqv9O5obI
 *
 * This uses the Common Metrics Library, which is an abstraction
 * to easily allow us to use Turnstile for now and migrate to whichever
 * new service we want in the future, such as DataDog.
 *
 * @see CommonMetricsLibrary
 */
class ContentEngagementMetricsReporter {
    constructor() {
        this._trackEmployerContent();
        this._trackContentPageCopy();
        this._trackVideoWatch();
        this._trackToolInteraction();
    }

    /**
     * Sample metrics
     * component: ContentEngagementMetrics
     * action: content-engagement-click
     * element: smb-cta-download-pdf or smb-cta-download-word
     * path: page and template
     */
    _trackEmployerContent() {
        const pdfDownloadButtonElementAttribute = "smb-cta-download-pdf";
        const pdfButtons = document.querySelectorAll(`[data-tn-element=${pdfDownloadButtonElementAttribute}]`);
        pdfButtons.forEach(pdfButton => pdfButton.addEventListener("click", function() {
            window.commonMetricsLibrary.report({
                "data-tn-element": pdfDownloadButtonElementAttribute,
                "data-tn-action": "content-engagement-click",
            });
        }, { once: true }));

        const wordDownloadButtonElementAttribute = "smb-cta-download-word";
        const wordButtons = document.querySelectorAll(`[data-tn-element=${wordDownloadButtonElementAttribute}]`);
        wordButtons.forEach(wordButton => wordButton.addEventListener("click", function() {
            window.commonMetricsLibrary.report({
                "data-tn-element": wordDownloadButtonElementAttribute,
                "data-tn-action": "content-engagement-click",
            });
        }, { once: true }));
    }

    /**
     * Sample metrics
     * component: ContentEngagementMetrics
     * action: content-engagement-copy
     * element: tag1#id1.class1.class2|tag2#id2.class1.class3
     * path: page and template
     */
    _trackContentPageCopy() {
        const mainElement = document.querySelector("main") ?? document.querySelector("#main"); // Some templates use a 'main' element, others use a div with id='main'.
        if (!mainElement) {
            return;
        }

        // We deliberately use an arrow function here since they still have access to the enclosing 'this' context
        // unlike anonymous functions via the traditional function expression.
        // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions
        const debouncedHandleCopyEvent = this.#debounce(() => {
            const highlightedText = document.getSelection().toString();
            if (highlightedText.length === 0) { // The copy event fires on ctrl+c even if no text is highlighted.
                return;
            }

            const truncatedText = this.#truncateMiddleText(highlightedText);
            window.commonMetricsLibrary.report({
                "data-tn-element": "content",
                "data-tn-action": "content-engagement-copy",
                "data-tn-component": truncatedText
            });
        });
        mainElement.addEventListener("copy", () => debouncedHandleCopyEvent()); // We intentionally do not specify 'once' so we can capture all copy events on a page.
    }

    /**
     * Truncates the middle section of the string using named capture groups with a non-greedy match for the middle.
     * If the string does not need to be truncated, it's returned as is.
     *
     * Private instance method
     * Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes/Private_class_fields
     *
     * @param text some string to truncate
     * @returns string the string with the middle section replaced with ellipses
     */
    #truncateMiddleText(text) {
        const MAX_TRUNCATED_TEXT_LENGTH = 400; // An arbitrary value.
        if (text.length <= MAX_TRUNCATED_TEXT_LENGTH) {
            return text;
        }

        const HALF_CONTEXT_LENGTH = MAX_TRUNCATED_TEXT_LENGTH / 2; // We have to split the desired amount between the first and last sentences.
        return text.slice(0, HALF_CONTEXT_LENGTH) + " ... " + text.slice(-HALF_CONTEXT_LENGTH);
    }

    /**
     * Sample metrics
     * component: ContentEngagementMetrics
     * action: content-engagement-play/content-engagement-watch-(15|30|60|120)s/content-engagement-watch-(50|100)%
     * element: wistia
     * path: page and template
     *
     * Wistia API reference: https://wistia.com/support/developers/player-api
     * Note: When using the Wistia API on the client rather than the video directly,
     *       the first value passed into the callback for every event listener
     *       is the embed itself, which has access to the Wistia functions.
     *       If you want to use the parameters as described in the API documentation,
     *       define an unused variable for each function such as function(_, desiredFirstValue, ...).
     */
    _trackVideoWatch() {
        if (typeof wistiaEmbeds === "undefined" || !wistiaEmbeds) { // wistiaEmbeds is defined on any page with a Wistia video.
            return;
        }

        // 1. We track if the video has been played.
        const element = "wistia";
        wistiaEmbeds.bind("play", function() {
            window.commonMetricsLibrary.report({
                "data-tn-element": element,
                "data-tn-action": "content-engagement-play",
            });
            return wistiaEmbeds.unbind; // Unbinds after firing once.
        });

        // 2. We track milestones for duration watched.
        // Design note: We do this because there's no guarantee the user finishes or pauses
        //              the video before navigating away, so we wouldn't be able to emit
        //              the metrics on a guaranteed event.
        //              These values were proposed by Tal.
        // We can bind and unbind these separately so we don't have a leftover event listener.
        const WATCH_DURATION_MILESTONES_IN_SECONDS = [15, 30, 60, 120];
        WATCH_DURATION_MILESTONES_IN_SECONDS.forEach((milestoneInSeconds) => {
            wistiaEmbeds.bind("secondchange", function(_, secondsElapsed) {
                if (secondsElapsed >= milestoneInSeconds) {
                    window.commonMetricsLibrary.report({
                        "data-tn-element": element,
                        "data-tn-action": `content-engagement-watch-${milestoneInSeconds}s`,
                    });
                    return wistiaEmbeds.unbind;
                }
            });
        });

        // 3. We track the halfway mark of the video.
        wistiaEmbeds.bind("percentwatchedchanged", function(_, percent, lastPercent) {
            if (percent >= 0.5 && lastPercent < 0.5) {
                window.commonMetricsLibrary.report({
                    "data-tn-element": element,
                    "data-tn-action": "content-engagement-watch-50%",
                });
                return wistiaEmbeds.unbind;
            }
        });

        // 4. We track that the user has finished watching the video.
        wistiaEmbeds.bind("end", function() {
            window.commonMetricsLibrary.report({
                "data-tn-element": element,
                "data-tn-action": "content-engagement-watch-100%",
            });
            return wistiaEmbeds.unbind;
        });
    }

    /**
     * component: ContentEngagementMetrics
     * action: content-engagement-tool-interaction
     * element: hourly-to-salary-calculator/salary-to-hourly-calculator/insights-tooltip
     * path: page and template
     */
    _trackToolInteraction() {
        const TOOLS_TO_TRACK = [
            // The hourly to salary calculator in the Employer Calculator template.
            // https://code.corp.indeed.com/wpdev/indeeddev/-/blob/development/wp-content/themes/tophat-theme/templates/employer-calculator/templates/partials/_calculator.php
            {
                element: "hourly-to-salary-calculator",
                selector: "#hourly-pay",
                event: "change"
            },
            // The salary to hourly calculator in the Employer Calculator template.
            // https://code.corp.indeed.com/wpdev/indeeddev/-/blob/development/wp-content/themes/tophat-theme/templates/employer-calculator/templates/partials/_calculator.php
            {
                element: "salary-to-hourly-calculator",
                selector: "#rvs-annual-pay",
                event: "change"
            },
            // The tooltips created by the job title modal Javascript, matched by prefix.
            // https://code.corp.indeed.com/wpdev/indeeddev/blob/development/wp-content/themes/tophat-theme/templates/shared/components/job-title-modal/job-title-modal.php
            {
                element: "insights-tooltip",
                selector: "[id^='jobTitleModal_']",
                event: "click"
            },
        ];
        TOOLS_TO_TRACK.forEach((toolToTrack) => {
            const toolInstances = document.querySelectorAll(toolToTrack.selector);
            toolInstances.forEach(toolInstance => toolInstance.addEventListener(toolToTrack.event, function() {
                window.commonMetricsLibrary.report({
                    "data-tn-element": toolToTrack.element,
                    "data-tn-action": "content-engagement-tool-interaction",
                });
            }, { once: true }));
        });
    }

    /**
     * Creates a debounced function with the specified timeout.
     * The function only fires after the timeout value has elapsed,
     * so multiple sequential actions only result in one call.
     *
     * @param func the function to debounce
     * @param timeout the time, in milliseconds, to wait before firing the specified function; defaults to 1 second
     * @returns {(function(...[*]): void)|*} the debounced function to use instead of the desired function directly
     */
    #debounce(func, timeout = 1000) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => { func.apply(this, args); }, timeout);
        };
    }
}
window.addEventListener("load", () => {
    const metricsReporter = new ContentEngagementMetricsReporter();
}, {once: true});
